import type { SortDirection } from "@trackmark/core";
import styles from "./Sort.module.css";

/**
 * Sorting, as two controls over one idea.
 *
 * `SortHead` is a column heading you can press; `SortRow` is the same thing for the covers grid,
 * which has no headings to press. Both say the same two things — which column the list is in, and
 * which way — because a list ordered by something you cannot see is a list you have to guess at.
 *
 * The caret is `▾`/`▴`, the app's existing vocabulary (`MAP TO ▾` in settings), and it appears on
 * the active column ONLY. A caret on every heading is a row of decorations; on one it is a fact.
 */

export interface SortHeadProps {
  label: string;
  active: boolean;
  direction: SortDirection;
  onSort: () => void;
  align?: "left" | "right";
}

export function SortHead({ label, active, direction, onSort, align = "left" }: SortHeadProps) {
  return (
    <button
      type="button"
      className={styles.head}
      data-align={align}
      data-active={active || undefined}
      /*
       * The name carries the state, because the caret cannot: unread, `▴` is "black up-pointing
       * small triangle".
       *
       * `aria-sort` would be the exact word for this and is not allowed here — it belongs to a
       * `columnheader`, and these tables are CSS grids with no table semantics to hang one on.
       * An attribute that is ignored is worse than a name that is read.
       */
      aria-label={active ? `${label}, sorted ${direction === "asc" ? "ascending" : "descending"}` : `Sort by ${label}`}
      data-testid={`sort-${label.toLowerCase()}`}
      onClick={onSort}
    >
      {label}
      {active && <span className={styles.caret}>{direction === "asc" ? "▴" : "▾"}</span>}
    </button>
  );
}

export interface SortOption {
  field: string;
  label: string;
  /** Which way this column opens. A score opens at the top; a title opens at A. */
  initial?: SortDirection;
}

export function SortRow({
  options,
  active,
  direction,
  onSort,
}: {
  options: SortOption[];
  active: string;
  direction: SortDirection;
  onSort: (field: string, direction: SortDirection) => void;
}) {
  return (
    <div className={styles.row} data-testid="sort-row">
      <span className={styles.label}>SORT</span>
      {options.map((option) => (
        <SortHead
          key={option.field}
          label={option.label}
          active={option.field === active}
          direction={direction}
          onSort={() =>
            onSort(
              option.field,
              // Pressing the column you are on turns it around; pressing another starts it the
              // way that column is worth reading first.
              option.field === active ? (direction === "asc" ? "desc" : "asc") : (option.initial ?? "asc"),
            )
          }
        />
      ))}
    </div>
  );
}
